import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import SignatureCanvas from "react-signature-canvas";
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Link2, Eraser } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function TestBanner() {
  return (
    <div className="border-2 border-[#F59E0B] bg-[#FEF3C7]/10 rounded-lg p-3 flex items-start gap-3" data-testid="test-banner">
      <AlertTriangle size={18} className="text-[#F59E0B] mt-0.5 shrink-0" />
      <div className="text-[13px]">
        <b className="text-[#F59E0B]">⚠ TEST — NOT LEGALLY BINDING.</b> This is a test agreement for workflow verification only. No party is bound by anything herein.
      </div>
    </div>
  );
}

export default function AgreementView() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [form, setForm] = useState({ signer_name: "", signer_title: "", signer_email: "", signer_phone: "", typed_legal_name: "", ack: false });
  const sigRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/a/${token}`);
        if (r.status === 404) { setErr("invalid"); setLoading(false); return; }
        if (r.status === 410) {
          const j = await r.json().catch(() => ({}));
          setErr(j?.detail === "revoked" ? "revoked" : "expired"); setLoading(false); return;
        }
        if (!r.ok) { setErr("network"); setLoading(false); return; }
        const j = await r.json();
        setD(j);
        setForm((f) => ({ ...f,
          signer_name: j.signer_name || "", signer_title: j.signer_title || "",
          signer_email: j.signer_email || "", signer_phone: j.signer_phone || "",
        }));
        if (j.already_signed) setSigned(true);
      } catch { setErr("network"); }
      setLoading(false);
    })();
  }, [token]);

  const canSign = form.signer_name.trim() && form.signer_email.trim()
    && form.typed_legal_name.trim() && form.ack
    && sigRef.current && !sigRef.current.isEmpty();

  async function submit() {
    setFormErr("");
    if (!canSign) { setFormErr("Complete all fields, check the acknowledgment, and draw your signature."); return; }
    setSubmitting(true);
    const png = sigRef.current.getCanvas().toDataURL("image/png");
    const r = await fetch(`${API}/api/a/${token}/sign`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        typed_legal_name: form.typed_legal_name.trim(),
        signer_name: form.signer_name.trim(), signer_title: form.signer_title.trim(),
        signer_email: form.signer_email.trim(), signer_phone: form.signer_phone.trim(),
        acknowledgment_at: new Date().toISOString(),
        signature_png_base64: png,
      }),
    });
    setSubmitting(false);
    if (r.status === 409) { setSigned(true); return; }
    if (r.status === 410) { setErr("expired"); return; }
    if (!r.ok) { const j = await r.json().catch(() => ({})); setFormErr(j?.detail || "Could not submit"); return; }
    const j = await r.json();
    setD((prev) => ({ ...prev, reference_number: j.reference_number }));
    setSigned(true);
  }

  const humanFin = () => {
    if (!d) return "—";
    if (d.financial_pref === "revenue_share" && d.requested_commission_pct != null) return `Revenue Share — ${d.requested_commission_pct}%`;
    if (d.financial_pref === "fixed_rent" && d.requested_monthly_amount != null) return `$${d.requested_monthly_amount}/month`;
    if (d.financial_pref === "no_commission") return "No Location Commission";
    if (d.financial_pref === "negotiable") return "Terms Negotiable";
    return "—";
  };
  const humanExcl = () => ({ requested: "Exclusive Placement Requested", accepted: "Exclusive Placement Accepted", declined: "Non-Exclusive", unknown: "To be discussed" }[d?.exclusivity_pref || "unknown"]);

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Test Agreement" description="SpotBounty test agreement" path={`/a/${token}`} noindex />
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-3xl mx-auto px-5 md:px-6 py-4 flex items-center justify-between">
          <SBLogo size={18} />
          <div className="flex items-center gap-2 border border-[#FFC107]/40 bg-[#FFC107]/5 rounded-full px-3 py-1.5">
            <Link2 size={11} className="text-[#FFC107]" />
            <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#FFC107]">Secure Agreement</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-6 py-6 space-y-5">
        {loading && <div className="text-[#A1A1AA] flex items-center gap-2 py-8"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

        {err && (
          <div className="sb-card p-6 border-[#EF4444]/40 flex items-start gap-3" style={{ background: "rgba(239,68,68,.05)" }} data-testid="ag-error">
            <AlertTriangle size={18} className="text-[#EF4444] mt-0.5" />
            <div>
              <div className="text-[14px] font-black text-[#EF4444]">
                {err === "revoked" && "This agreement link has been revoked."}
                {err === "expired" && "This agreement link has expired."}
                {err === "invalid" && "This agreement link is invalid."}
                {err === "network" && "Could not load."}
              </div>
              <div className="text-[12px] text-[#A1A1AA] mt-1">Please contact your SpotBounty representative for a new link.</div>
            </div>
          </div>
        )}

        {d && !err && (
          <>
            <TestBanner />

            {signed ? (
              <div className="sb-card p-6 border-[#22C55E]/40" style={{ background: "rgba(34,197,94,.05)" }} data-testid="ag-signed">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#22C55E] mt-0.5" />
                  <div>
                    <div className="text-[16px] font-black">Agreement Signed</div>
                    <div className="text-[12px] text-[#A1A1AA] mt-1">Reference: <b className="text-white">{d.reference_number}</b></div>
                    <div className="text-[12px] text-[#A1A1AA]">Business: <b className="text-white">{d.business_name}</b></div>
                    <div className="text-[12px] text-[#A1A1AA]">Signer: <b className="text-white">{d.signer_name || form.signer_name}</b></div>
                    <div className="text-[12px] text-[#A1A1AA] mt-2">SpotBounty has been notified.</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <section className="sb-card p-5">
                  <div className="sb-eyebrow">Business</div>
                  <div className="mt-2 text-[18px] font-black" data-testid="ag-biz">{d.business_name}</div>
                  <div className="text-[12.5px] text-[#A1A1AA] mt-1">{d.business_address}</div>
                </section>

                <section className="sb-card p-5">
                  <div className="sb-eyebrow">Equipment &amp; Placement</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(d.machine_types || []).map((m) => <span key={m} className="sb-pill sb-pill-gold">{m}</span>)}
                  </div>
                  <div className="mt-3 text-[13px] text-white/90">{d.placement_description || <span className="text-[#52525b]">—</span>}</div>
                </section>

                <section className="sb-card p-5 grid sm:grid-cols-3 gap-4">
                  <div><div className="sb-eyebrow">Financial Terms</div><div className="mt-2 text-[14px] font-bold" data-testid="ag-fin">{humanFin()}</div></div>
                  <div><div className="sb-eyebrow">Term</div><div className="mt-2 text-[14px] font-bold">{d.preferred_term_months ? `${d.preferred_term_months} Months` : (d.preferred_term_custom || "—")}</div></div>
                  <div><div className="sb-eyebrow">Exclusivity</div><div className="mt-2 text-[14px] font-bold">{humanExcl()}</div></div>
                </section>

                <section className="sb-card p-5">
                  <div className="sb-eyebrow">Agreement Content</div>
                  <div className="mt-3 max-h-[420px] overflow-auto border border-[#2A2F34] rounded p-5"
                       style={{ background: "#F5F5F5", color: "#111", fontSize: 16, lineHeight: 1.7 }}
                       data-testid="ag-body"
                       dangerouslySetInnerHTML={{ __html: d.snapshot_html || "" }} />
                </section>

                <section className="sb-card p-5">
                  <div className="sb-eyebrow">Signer Information</div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <label className="block"><div className="sb-label">Full legal name *</div>
                      <input className="sb-input" value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} data-testid="ag-signer-name" /></label>
                    <label className="block"><div className="sb-label">Title</div>
                      <input className="sb-input" value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })} /></label>
                    <label className="block"><div className="sb-label">Email *</div>
                      <input type="email" className="sb-input" value={form.signer_email} onChange={(e) => setForm({ ...form, signer_email: e.target.value })} data-testid="ag-signer-email" /></label>
                    <label className="block"><div className="sb-label">Phone</div>
                      <input className="sb-input" value={form.signer_phone} onChange={(e) => setForm({ ...form, signer_phone: e.target.value })} /></label>
                  </div>
                </section>

                <TestBanner />

                <section className="sb-card p-5">
                  <label className="flex items-start gap-3 text-[13px]">
                    <input type="checkbox" className="mt-1" checked={form.ack} onChange={(e) => setForm({ ...form, ack: e.target.checked })} data-testid="ag-ack" />
                    <span>I confirm that I am authorized to act on behalf of the listed business, that I have reviewed the agreement above, and that my electronic signature below represents my signature.</span>
                  </label>
                </section>

                <section className="sb-card p-5">
                  <div className="sb-eyebrow">Sign Below</div>
                  <div className="mt-3 border border-[#2A2F34] rounded-lg bg-white overflow-hidden"
                       style={{ height: 200, touchAction: "none" }}
                       onTouchStart={(e) => e.preventDefault()}
                       data-testid="ag-sig-container">
                    <SignatureCanvas ref={sigRef} penColor="#111"
                      canvasProps={{ width: 360, height: 200, style: { width: "100%", height: "100%", display: "block", touchAction: "none" }, "data-testid": "ag-sig-canvas" }} />
                  </div>
                  <div className="mt-3 flex justify-between items-center text-[11.5px]">
                    <button onClick={() => sigRef.current && sigRef.current.clear()} type="button"
                            className="text-[#A1A1AA] hover:text-white flex items-center gap-1"
                            style={{ minHeight: 44, minWidth: 44, padding: "8px 12px" }}
                            data-testid="ag-sig-clear"><Eraser size={12} /> Clear signature</button>
                  </div>
                  <label className="block mt-4"><div className="sb-label">Type your full legal name *</div>
                    <input className="sb-input" style={{ fontSize: 16 }} value={form.typed_legal_name} onChange={(e) => setForm({ ...form, typed_legal_name: e.target.value })} data-testid="ag-typed" /></label>
                </section>

                {formErr && <div className="text-[12px] text-[#EF4444]" data-testid="ag-form-error">{formErr}</div>}

                <button disabled={submitting || !canSign} onClick={submit}
                        className="sb-btn-primary w-full text-[12px]" style={{ padding: "16px 16px" }} data-testid="ag-submit">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Sign Test Agreement
                </button>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
